import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BiErrorCircle,
  BiError,
} from 'react-icons/bi'; /** The MIT License (MIT)
Copyright (c) 2015-2021 Aniket Suvarna

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
 */
import styles from './modal.module.css';

/**
 *
 * @param {Object} props - 모달에 필요한 속성을 포함한 객체
 * @param {string} props.info - 정보 형식의 모달 (옵션)
 * @param {string} props.error - 모달에 표시할 오류 메시지 (옵션)
 * @param {string} props.title - 모달의 제목 (옵션)
 *
 * @param {Object} props.closeProps - 모달 닫기 버튼 속성 (옵션)
 * @param {boolean} props.closeProps.disabled - 모달 닫기 버튼 비활성화 여부
 * @param {boolean} props.closeProps.onClick - 모달 닫기 버튼 클릭 이벤트
 *
 * @param {Object} props.applyProps - 모달에서 적용 버튼 속성 (옵션)
 * @param {boolean} props.applyProps.disabled - 모달 적용 버튼 비활성화 여부
 * @param {boolean} props.applyProps.onClick - 모달 적용 버튼 클릭 이벤트
 *
 * @param {Object} props.cancelProps - 모달에서 취소 버튼 속성 (옵션)
 * @param {boolean} props.cancelProps.disabled - 모달 취소 버튼 비활성화 여부
 * @param {boolean} props.cancelProps.onClick - 모달 취소 버튼 클릭 이벤트
 *
 * @param {React.ReactNode} props.children - 모달 내용으로 표시할 ReactNode (필수)
 * @returns {JSX.Element} - 생성된 모달 컴포넌트
 */
const Modal = ({
  info,
  error,
  title,
  closeProps,
  applyProps,
  cancelProps,
  children,
  ...rest
}) => {
  const outside = useRef();
  const onClickOutside = (e) => {
    if (e.target === outside.current) {
      closeProps?.onClick && closeProps.onClick();
      cancelProps?.onClick && cancelProps.onClick();
    }
  };

  useEffect(() => {
    if (document.querySelectorAll('[class*=modalView]').length < 2) {
      document.body.style = 'overflow: hidden';
      return () => (document.body.style = 'overflow: auto');
    }
  }, []);

  return (
    <div className={styles.modalOverlay} ref={outside} onClick={onClickOutside}>
      <div className={styles.modalView} {...rest}>
        <ModalHeader {...{ info, error, title }} />
        {children}
        <ButtonGroup {...{ closeProps, applyProps, cancelProps }} />
      </div>
    </div>
  );
};

const ModalHeader = ({ info, error, title }) => {
  return (
    <div className={styles.modalHeader}>
      {info && <BiErrorCircle size={30} color="gray" />}
      {error && <BiError size={30} color="red" />}
      {title && (info || error) ? (
        <h2 dangerouslySetInnerHTML={{ __html: title }} />
      ) : (
        <h3 dangerouslySetInnerHTML={{ __html: title }} />
      )}
    </div>
  );
};

const ButtonGroup = ({ closeProps, applyProps, cancelProps }) => {
  const { t } = useTranslation();
  const isShow = closeProps || applyProps || cancelProps;

  return (
    isShow && (
      <div className={styles.buttonGroup}>
        {closeProps && (
          <button {...closeProps} className={'common'}>
            <b>{t('btn_close')}</b>
          </button>
        )}
        {cancelProps && (
          <button {...cancelProps} className={'common'}>
            <b>{t('btn_cancel')}</b>
          </button>
        )}
        {applyProps && (
          <button {...applyProps} className={'apply'}>
            <b>{t('btn_apply')}</b>
          </button>
        )}
      </div>
    )
  );
};

export default Modal;
